<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('shortDomains.settings') }}

                <div
                    v-if="isLoaded"
                    class="pull-right"
                >
                    <router-link
                        :to="{ name: 'short-domains.list' }"
                        class="btn btn-sm btn-default"
                    >
                        <span class="sr-only">{{ $t('shortDomains.shortDomainsList') }}</span>
                        <span class="hidden-xs">{{ $t('shortDomains.shortDomainsList') }}</span>
                        <span class="visible-xs">
                            <i class="fa fa-list-ul"></i>
                        </span>
                    </router-link>
                </div>
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div
                v-if="!isLoaded"
                class="row"
            >
                <div class="col-xs-12">
                    <div class="box box-primary">
                        <div class="box-body">
                            <img
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <template v-else>
                <div class="row">
                    <div class="col-xs-12">
                        <div class="box box-primary">
                            <div class="box-header with-border">
                                <h3 class="box-title">TLD Settings</h3>

                                <div class="box-tools pull-right">
                                    <button
                                        class="btn btn-box-tool"
                                        :title="$t('collapse')"
                                        type="button"
                                        @click="isCollapseTld = !isCollapseTld"
                                    >
                                        <i
                                            class="fa"
                                            :class="{
                                                'fa-plus': !isCollapseTld,
                                                'fa-minus': isCollapseTld,
                                            }"
                                        />
                                    </button>
                                </div>
                            </div>

                            <vue-slide-up-down :active="isCollapseTld">
                                <div class="box-body table-responsive no-padding">
                                    <table class="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th
                                                    v-for="(item, idx) in settings.dTableHeader"
                                                    :key="idx"
                                                    :width="idx === 0 ? '40%' : `${60 / (settings.dTableHeader.length - 1)}%`"
                                                >
                                                    {{ item }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="(row, rowIdx) in settings.dTable"
                                                :key="rowIdx"
                                                :class="{
                                                    'bg-orange': settings.orangeIps.includes(row[0]),
                                                    'bg-red': settings.redIps.includes(row[0]),
                                                }"
                                            >
                                                <td
                                                    v-for="(item, itemIdx) in row"
                                                    :key="itemIdx"
                                                >
                                                    {{ item }}
                                                </td>
                                            </tr>
                                        </tbody>
                                        <tfoot>
                                            <tr
                                                v-for="(row, idx) in settings.dTableFooter"
                                                :key="idx"
                                            >
                                                <td>
                                                    <b>{{ row[0] }}</b>
                                                </td>
                                                <td
                                                    v-for="tld in settings.tlds"
                                                    :key="tld.id"
                                                >
                                                    <b>{{ idx === 'price' ? formatPrice(tld[idx]) : tld[idx] }}</b>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                                <div class="box-footer clearfix">
                                    <h4>{{ `${$t('shortDomains.tldSettings')} - ${$t('edit')}` }}</h4>

                                    <ValidationObserver
                                        v-slot="{ invalid }"
                                        ref="observerTld"
                                        slim
                                    >
                                        <div class="row">
                                            <div class="col-xs-12 col-sm-5 col-lg-3">
                                                <ValidationProvider
                                                    v-slot="{ errors }"
                                                    name="tld id"
                                                    vid="tld_id"
                                                    rules="required"
                                                    slim
                                                >
                                                    <div
                                                        class="form-group"
                                                        :class="{ 'has-error': errors.length }"
                                                    >
                                                        <label>{{ $t('shortDomains.tld') }} <b class="text-danger">*</b></label>

                                                        <FormSelect
                                                            v-model="settingsTld.tld"
                                                            :options="settings.tldsUnique"
                                                            :placeholder="`${$t('select')} ${$t('shortDomains.tld')}`"
                                                            :disabled="isLoading"
                                                            closeOnSelect
                                                            allowEmpty
                                                            trackBy="id"
                                                            label="name"
                                                            @input="setTld"
                                                        />

                                                        <div
                                                            v-if="errors.length"
                                                            class="help-block"
                                                            v-text="errors[0]"
                                                        />
                                                    </div>
                                                </ValidationProvider>
                                            </div>

                                            <div class="col-xs-12 col-sm-6 col-lg-3">
                                                <ValidationProvider
                                                    v-slot="{ errors }"
                                                    name="max messages"
                                                    vid="max_messages"
                                                    rules="required|min_value:0"
                                                    slim
                                                >
                                                    <div
                                                        class="form-group"
                                                        :class="{ 'has-error': errors.length }"
                                                    >
                                                        <label>{{ $t('shortDomains.maxMessages') }} <b class="text-danger">*</b></label>

                                                        <input
                                                            v-model="settingsTld.maxMessages"
                                                            type="number"
                                                            class="form-control"
                                                            :placeholder="$t('shortDomains.maxMessages')"
                                                            :disabled="isLoading || !isTldEnabled"
                                                            min="0"
                                                            step="1"
                                                            @keypress="onKeypressTldInput"
                                                        />

                                                        <div
                                                            v-if="errors.length"
                                                            class="help-block"
                                                            v-text="errors[0]"
                                                        />
                                                    </div>
                                                </ValidationProvider>
                                            </div>

                                            <div class="col-xs-12 col-sm-5 col-lg-3">
                                                <ValidationProvider
                                                    v-slot="{ errors }"
                                                    name="price"
                                                    rules="required|min_value:0|max_value:1000"
                                                    slim
                                                >
                                                    <div
                                                        class="form-group"
                                                        :class="{ 'has-error': errors.length }"
                                                    >
                                                        <label>{{ $t('shortDomains.price') }} <b class="text-danger">*</b></label>

                                                        <input
                                                            v-model="settingsTld.price"
                                                            type="number"
                                                            class="form-control"
                                                            :placeholder="$t('shortDomains.price')"
                                                            :disabled="isLoading || !isTldEnabled"
                                                            min="0"
                                                            step="0.1"
                                                            @keypress="onKeypressTldInput"
                                                        />

                                                        <div
                                                            v-if="errors.length"
                                                            class="help-block"
                                                            v-text="errors[0]"
                                                        />
                                                    </div>
                                                </ValidationProvider>
                                            </div>

                                            <div class="col-xs-12 col-sm-6 col-lg-3">
                                                <label class="col-xs-12 hidden-xs">&nbsp;</label>

                                                <button
                                                    type="button"
                                                    class="btn btn-sm btn-primary"
                                                    :disabled="isLoading || invalid"
                                                    @click="submitSettingsTld"
                                                >
                                                    <i
                                                        v-if="isLoading"
                                                        class="fa fa-refresh fa-spin"
                                                    />
                                                    <span>{{ $t('save') }}</span>
                                                </button>

                                                <button
                                                    v-if="isTldEnabled"
                                                    class="btn btn-sm btn-default"
                                                    :disabled="isLoading"
                                                    @click="resetSettingsTld"
                                                >
                                                    {{ $t('cancel') }}
                                                </button>
                                            </div>
                                        </div>
                                    </ValidationObserver>
                                </div>
                            </vue-slide-up-down>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-xs-12">
                        <div class="box box-primary">
                            <div class="box-header with-border">
                                <h3 class="box-title">{{ $t('shortDomains.carriersSettings') }}</h3>

                                <div class="box-tools pull-right">
                                    <button
                                        class="btn btn-box-tool"
                                        :title="$t('collapse')"
                                        type="button"
                                        @click="isCollapseCarrier = !isCollapseCarrier"
                                    >
                                        <i
                                            class="fa"
                                            :class="{
                                                'fa-plus': !isCollapseCarrier,
                                                'fa-minus': isCollapseCarrier,
                                            }"
                                        />
                                    </button>
                                </div>
                            </div>

                            <vue-slide-up-down :active="isCollapseCarrier">
                                <div class="box-body table-responsive no-padding">
                                    <table class="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th
                                                    v-for="(item, idx) in carrierSettingTableHeader"
                                                    :key="idx"
                                                    :width="idx === 0 ? '40%' : '30%'"
                                                >
                                                    {{ item }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="(row, rowIdx) in carrierSettingTable"
                                                :key="rowIdx"
                                                :class="{
                                                    'bg-orange': settings.orangeIps.includes(row[0]),
                                                    'bg-red': settings.redIps.includes(row[0]),
                                                }"
                                            >
                                                <td
                                                    v-for="(col, colIdx) in row"
                                                    :key="colIdx"
                                                >
                                                    <span v-if="colIdx === 0">{{ col }}</span>
                                                    <template v-else-if="col[0]">
                                                        <code
                                                            v-for="(item, itemIdx) in col"
                                                            :key="itemIdx"
                                                            style="margin-right: 4px"
                                                        >
                                                            {{ item }}
                                                        </code>
                                                    </template>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div class="box-footer clearfix">
                                    <h4>{{ `${$t('shortDomains.carriersSettings')} - ${$t('edit')}` }}</h4>

                                    <ValidationObserver
                                        ref="observerCarrier"
                                        tag="div"
                                    >
                                        <div class="row">
                                            <div class="col-xs-12 col-sm-6 col-lg-3">
                                                <ValidationProvider
                                                    v-slot="{ errors }"
                                                    name="carrier"
                                                    vid="carrier_id"
                                                    rules="required"
                                                    slim
                                                >
                                                    <div
                                                        class="form-group"
                                                        :class="{ 'has-error': errors.length }"
                                                    >
                                                        <label>{{ $t('shortDomains.carrier') }}</label>

                                                        <FormSelect
                                                            v-model="settingsCarrierType"
                                                            :options="settings.carrierTypes"
                                                            :placeholder="`${$t('select')} ${$t('shortDomains.carrier')}`"
                                                            :disabled="isLoading"
                                                            closeOnSelect
                                                            allowEmpty
                                                            trackBy="id"
                                                            label="name"
                                                            @input="setCarrier"
                                                        />

                                                        <div
                                                            v-if="errors.length"
                                                            class="help-block"
                                                            v-text="errors[0]"
                                                        />
                                                    </div>
                                                </ValidationProvider>
                                            </div>
                                        </div>

                                        <div class="row">
                                            <div class="col-xs-12 col-lg-6">
                                                <ValidationProvider
                                                    v-slot="{ errors }"
                                                    name="TLDs"
                                                    vid="tld_ids"
                                                    slim
                                                >
                                                    <div
                                                        class="form-group"
                                                        :class="{ 'has-error': errors.length }"
                                                    >
                                                        <label>{{ $t('shortDomains.tlds') }}</label>

                                                        <FormSelect
                                                            v-model="settingsCarrierTlds"
                                                            :options="settings.tldsUnique"
                                                            :placeholder="`${$t('select')} ${$t('shortDomains.tlds')}`"
                                                            :disabled="isLoading || !isCarrierEnabled"
                                                            allowEmpty
                                                            searchable
                                                            multiple
                                                            taggable
                                                            trackBy="id"
                                                            label="name"
                                                        />

                                                        <div
                                                            v-if="errors.length"
                                                            class="help-block"
                                                            v-text="errors[0]"
                                                        />
                                                    </div>
                                                </ValidationProvider>
                                            </div>
                                        </div>

                                        <div class="row">
                                            <div class="col-xs-12 col-lg-6">
                                                <ValidationProvider
                                                    v-slot="{ errors }"
                                                    name="ip ids"
                                                    vid="ip_ids"
                                                    slim
                                                >
                                                    <div
                                                        class="form-group"
                                                        :class="{ 'has-error': errors.length }"
                                                    >
                                                        <label>{{ $t('shortDomains.ips') }}</label>

                                                        <FormSelect
                                                            v-model="settingsCarrierIps"
                                                            :options="settings.ips"
                                                            :placeholder="`${$t('select')} ${$t('shortDomains.ips')}`"
                                                            :disabled="isLoading || !isCarrierEnabled"
                                                            allowEmpty
                                                            searchable
                                                            multiple
                                                            taggable
                                                            trackBy="id"
                                                            label="name"
                                                        />

                                                        <div
                                                            v-if="errors.length"
                                                            class="help-block"
                                                            v-text="errors[0]"
                                                        />
                                                    </div>
                                                </ValidationProvider>
                                            </div>
                                        </div>

                                        <div class="row">
                                            <div class="col-xs-12">
                                                <button
                                                    type="button"
                                                    class="btn btn-sm btn-primary"
                                                    :disabled="isLoading || !isCarrierEnabled"
                                                    @click="submitSettingsCarrier"
                                                >
                                                    <i
                                                        v-if="isLoading"
                                                        class="fa fa-refresh fa-spin"
                                                    />
                                                    <span>{{ $t('save') }}</span>
                                                </button>

                                                <button
                                                    v-if="isCarrierEnabled"
                                                    class="btn btn-sm btn-default"
                                                    :disabled="isLoading"
                                                    @click="resetSettingsCarrier"
                                                >
                                                    {{ $t('cancel') }}
                                                </button>
                                            </div>
                                        </div>
                                    </ValidationObserver>
                                </div>
                            </vue-slide-up-down>
                        </div>
                    </div>
                </div>
            </template>
        </section>
    </div>
</template>

<script>
import EventBus from '@/event-bus';

export default {
    data() {
        return {
            isLoading: false,
            isCollapseTld: true,
            isCollapseCarrier: true,
            isLoaded: false,
            isTldEnabled: false,
            isCarrierEnabled: false,
            settings: {},
            settingsTld: {},
            settingsCarrierType: null,
            settingsCarrierTlds: [],
            settingsCarrierIps: [],
        };
    },
    computed: {
        carrierSettingTableHeader() {
            return this.settings.carrierSettingTable[0];
        },
        carrierSettingTable() {
            const carrierSettingTable = { ...this.settings.carrierSettingTable };
            delete carrierSettingTable[0];
            return carrierSettingTable;
        },
    },
    created() {
        this.fetchSettings();
    },
    methods: {
        async fetchSettings() {
            try {
                const { data: settings } = await axios.get('/short-domains/settings/data');

                settings.tldsUnique = this.fromObjectToArray(settings.tldsUnique);
                settings.ips = this.fromObjectToArray(settings.ips);
                settings.carrierTypes = this.fromObjectToArray(settings.carrierTypes);

                this.settings = settings;
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoaded = true;
            }
        },
        setTld(selected) {
            if (!selected) {
                return this.resetSettingsTld();
            }

            const tld = this.settings.tlds[selected.id];

            this.isTldEnabled = true;
            this.settingsTld.maxMessages = tld.maxMessages || 0;
            this.settingsTld.price = tld.price || 0;
        },
        async submitSettingsTld() {
            if (!(await this.$refs.observerTld.validate())) {
                return;
            }

            try {
                this.isLoading = true;

                const requestData = {
                    tldId: this.settingsTld.tld.id,
                    maxMessages: this.settingsTld.maxMessages,
                    price: this.settingsTld.price,
                };

                const {
                    data: { tldId, maxMessages, price },
                } = await axios.post('/short-domains/settings/data/tld', requestData);

                this.settings.tlds[tldId].maxMessages = maxMessages;
                this.settings.tlds[tldId].price = parseFloat(price);

                this.resetSettingsTld();
            } catch (error) {
                const {
                    data: { errors, message },
                } = error.response;

                if (errors) {
                    this.$refs.observerTld.setErrors(errors);
                }

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message,
                    errors,
                });
            } finally {
                this.isLoading = false;
            }
        },
        resetSettingsTld() {
            this.$refs.observerTld.reset();
            this.isTldEnabled = false;
            this.settingsTld = {};
        },
        setCarrier(selected) {
            if (!selected) {
                return this.resetSettingsCarrier();
            }

            this.$refs.observerCarrier.reset();
            const selectedSetting = this.settings.settings[selected.id];

            if (selectedSetting) {
                if (selectedSetting.tldIds) {
                    const selectedTlds = Object.values(selectedSetting.tldIds).map((id) => {
                        return { ...(this.settings.tldsUnique.find((e) => e.id === parseInt(id)) || {}) };
                    });

                    this.settingsCarrierTlds = selectedTlds || [];
                }

                if (selectedSetting.ipIds) {
                    const selectedIps = Object.values(selectedSetting.ipIds).map((id) => {
                        return { ...(this.settings.ips.find((e) => e.id === parseInt(id)) || {}) };
                    });

                    this.settingsCarrierIps = selectedIps || [];
                }

                this.isCarrierEnabled = true;
            } else {
                this.isCarrierEnabled = false;
                this.settingsCarrierTlds = [];
                this.settingsCarrierIps = [];
            }
        },
        async submitSettingsCarrier() {
            if (!(await this.$refs.observerCarrier.validate())) {
                return;
            }

            try {
                this.isLoading = true;

                const requestData = {
                    carrierId: this.settingsCarrierType.id,
                    tldIds: this.settingsCarrierTlds.map((e) => String(e.id)),
                    ipIds: this.settingsCarrierIps.map((e) => String(e.id)),
                };

                const {
                    data: { carrierId, tldIds, ipIds },
                } = await axios.post('/short-domains/settings/data/carrier', requestData);

                this.settings.carrierSettingTable[carrierId][1] = this.settingsCarrierTlds.map((e) => e.name);
                this.settings.carrierSettingTable[carrierId][2] = this.settingsCarrierIps.map((e) => e.name);
                this.settings.settings[carrierId].tldIds = tldIds;
                this.settings.settings[carrierId].ipIds = ipIds;
            } catch (error) {
                const {
                    data: { errors, message },
                } = error.response;

                if (errors) {
                    this.$refs.observerCarrier.setErrors(errors);
                }

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message,
                    errors,
                });
            } finally {
                this.isLoading = false;
            }
        },
        resetSettingsCarrier() {
            this.$refs.observerCarrier.reset();
            this.isCarrierEnabled = false;
            this.settingsCarrierType = null;
            this.settingsCarrierTlds = [];
            this.settingsCarrierIps = [];
        },
        fromObjectToArray(obj) {
            return Object.entries(obj).map(([id, name]) => {
                return {
                    id: parseInt(id),
                    name,
                };
            });
        },
        formatPrice(price) {
            return new Intl.NumberFormat('en', { minimumFractionDigits: 2 }).format(price.toFixed(2));
        },
        onKeypressTldInput(e) {
            if (!String.fromCharCode(e.which).match(/[0-9]/)) {
                e.preventDefault();
                return false;
            }
        },
    },
};
</script>
